import { useCallback, useEffect, useMemo } from 'react';
import { Alert, AlertIcon, Center, Spinner, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { DataTableSchema } from '@duke-hq/ui/components/DataTable/schema';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { withPermission } from '../withPermission';
import { AutomationRuleDrawer } from './AutomationRuleDrawer';
import { formatTaskType } from '../SupportTasks/utils/formatters';
import { AutomationRuleFilters } from './AutomationRuleFilters';
import {
  describeScope,
  describeTrigger,
  ruleStatus,
  RULE_STATUS_KEYS,
  type RuleStatusKey,
  type RuleTaskKind,
} from './ruleDisplay';

export const AutomationRulesPage = withPermission('canManageAutomationRules', () => {
  return <AutomationRulesTable />;
});

function AutomationRulesTable() {
  const { ruleId } = useParams();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: rules, error } = trpcAdmin.automationRules.list.useQuery();

  // Filter state lives in the URL, as it does on the Support Tasks page, so a
  // filtered view survives a refresh and can be shared. SearchInput owns the
  // `search` param itself.
  const search = searchParams.get('search') ?? '';
  const kindParam = searchParams.get('kind');
  const statusParam = searchParams.get('status');

  const availableKinds = useMemo(() => (rules ?? []).map((rule) => rule.taskKind), [rules]);

  // Unrecognised params are ignored rather than filtering everything out.
  const kind = useMemo(
    () => availableKinds.find((value) => value === kindParam),
    [availableKinds, kindParam],
  );
  const status = RULE_STATUS_KEYS.find((value) => value === statusParam);

  const setFilterParam = useCallback(
    (key: 'kind' | 'status', value?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value == null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onKindChange = useCallback(
    (value?: RuleTaskKind) => setFilterParam('kind', value),
    [setFilterParam],
  );
  const onStatusChange = useCallback(
    (value?: RuleStatusKey) => setFilterParam('status', value),
    [setFilterParam],
  );

  const visibleRules = useMemo(() => {
    if (rules == null) {
      return;
    }
    const needle = search.trim().toLowerCase();
    // Matched by label, not by kind: the Type menu is deduped by label, so
    // picking "Withdrawal Warning EC" must keep both kinds behind that label.
    const kindLabel = kind == null ? null : formatTaskType(kind, null);
    return rules.filter((rule) => {
      if (kindLabel != null && formatTaskType(rule.taskKind, null) !== kindLabel) {
        return false;
      }
      if (status != null && ruleStatus(rule).key !== status) {
        return false;
      }
      return needle.length === 0 || rule.name.toLowerCase().includes(needle);
    });
  }, [rules, search, kind, status]);

  // Carrying the query string keeps the filters applied when a rule is opened
  // and closed again.
  const onCloseDrawer = useCallback(() => {
    navigate(
      { pathname: '/tasks/automation-rules', search: searchParams.toString() },
      {
        replace: true,
      },
    );
  }, [navigate, searchParams]);

  const openRule = rules?.find((rule) => rule.id === ruleId);

  // A bookmark to a rule that no longer exists would otherwise sit on a rule
  // URL showing the plain list, with no drawer and no way back to it.
  useEffect(() => {
    if (rules != null && ruleId != null && openRule == null) {
      onCloseDrawer();
    }
  }, [rules, ruleId, openRule, onCloseDrawer]);

  const onRowClick = useCallback(
    (rowId: string | number) => {
      navigate({
        pathname: `/tasks/automation-rules/${encodeURIComponent(rowId)}`,
        search: searchParams.toString(),
      });
    },
    [navigate, searchParams],
  );

  const data = useMemo(() => {
    if (visibleRules == null) {
      return;
    }
    return {
      headers: {
        name: { label: t`Name`, sortable: false },
        taskKind: { label: t`Type`, sortable: false },
        trigger: { label: t`Trigger`, sortable: false },
        scope: { label: t`Applies to`, sortable: false },
        status: { label: t`Status`, sortable: false },
      },
      rows: visibleRules.map((rule) => {
        const status = ruleStatus(rule);
        return {
          id: rule.id,
          cells: {
            name: rule.name,
            // Same enum the Support Tasks table shows as Type, so it is
            // labelled and formatted the same way here.
            taskKind: formatTaskType(rule.taskKind, null),
            trigger: describeTrigger(rule),
            scope: describeScope(rule),
            status: {
              type: 'status' as const,
              label: status.label,
              colorScheme: status.colorScheme,
            },
          },
        };
      }),
    } satisfies DataTableSchema;
  }, [visibleRules]);

  if (error != null) {
    return (
      <Alert status="error">
        <AlertIcon />
        <Trans>The automation rules could not be loaded.</Trans>
      </Alert>
    );
  }

  if (rules == null || data == null) {
    return (
      <Center p={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="automation-rules">
            <Trans>Automation Rules</Trans>
          </PageHeading>,
        ]}
        filters={
          <AutomationRuleFilters
            kind={kind}
            status={status}
            availableKinds={availableKinds}
            onKindChange={onKindChange}
            onStatusChange={onStatusChange}
          />
        }
        tableActionsLeft={null}
        table={
          <>
            <DataTable
              data={data}
              onSortChange={() => {}}
              onRowClick={onRowClick}
              tableProps={{
                colorScheme: 'purple',
                size: 'sm',
              }}
            />
            {data.rows.length === 0 && (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No rules found</Trans>
                </Text>
              </Center>
            )}
          </>
        }
        pagination={null}
      />
      <AutomationRuleDrawer rule={openRule} isOpen={openRule != null} onClose={onCloseDrawer} />
    </>
  );
}
